export const ADMIN_DECRYPT_REASONS = [
  "Bug investigation",
  "Customer support",
  "Billing dispute",
  "Safety review",
  "Other",
];

function cleanText(value, fallback = "-") {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === "not available") return fallback;
  return trimmed;
}

function roleEventLabel(role) {
  if (role === "user") return "Patient message";
  if (role === "assistant") return "Assistant reply";
  if (role === "human") return "Human reply";
  return "Conversation event";
}

function phoneDisplay(value) {
  return cleanText(value, "").replace(/^whatsapp:/, "") || "-";
}

function sessionKeyFor(row) {
  return [
    row.utilisateur || "unknown-user",
    row.praticien_id || "unknown-practitioner",
    row.twilio_numero || "unknown-inbox",
  ].join(":");
}

export function buildAdminConversationSessions(
  messages = [],
  clients = [],
  practitioners = [],
  accessLogs = [],
) {
  const clientByTwilio = new Map(
    clients.map((client) => [
      client.twilio_numero,
      cleanText(client.raison_sociale, "Unknown clinic"),
    ]),
  );
  const practitionerById = new Map(
    practitioners.map((practitioner) => [
      practitioner.id,
      cleanText(practitioner.full_name, "Unknown practitioner"),
    ]),
  );
  const loggedConversationIds = new Set(
    accessLogs.map((log) => log.conversation_id).filter(Boolean),
  );

  const sorted = [...messages].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  // Group every message by user (utilisateur), keeping the practitioner/inbox
  // dimension so each consolidated thread still maps to a single praticien_id
  // for the audited decryption request. Messages are processed in chronological
  // order, so the first occurrence of a key sets the conversation start and the
  // last occurrence sets the conversation end.
  const sessionsByKey = new Map();

  sorted.forEach((message) => {
    const sessionKey = sessionKeyFor(message);
    let current = sessionsByKey.get(sessionKey);

    if (!current) {
      current = {
        sessionKey,
        firstMessageId: message.id,
        utilisateur: message.utilisateur,
        praticienId: message.praticien_id,
        twilioNumero: message.twilio_numero,
        clinicName: clientByTwilio.get(message.twilio_numero) || "Unknown clinic",
        practitionerName:
          practitionerById.get(message.praticien_id) || "Unknown practitioner",
        patientName: cleanText(message.profile_name, "-"),
        phone: phoneDisplay(message.utilisateur),
        sessionStart: message.created_at,
        sessionEnd: message.created_at,
        messageIds: [],
        messageCount: 0,
        roles: [],
        lastEventLabel: roleEventLabel(message.role),
        hasAccessLog: false,
      };
      sessionsByKey.set(sessionKey, current);
    }

    current.messageIds.push(message.id);
    current.messageCount += 1;
    current.sessionEnd = message.created_at;
    current.lastEventLabel = roleEventLabel(message.role);
    current.roles = [...new Set([...current.roles, message.role].filter(Boolean))].sort();
    // Keep the patient name as soon as one is known, even if later rows are blank.
    if (current.patientName === "-") {
      current.patientName = cleanText(message.profile_name, "-");
    }
    current.hasAccessLog =
      current.hasAccessLog || loggedConversationIds.has(message.id);
  });

  return [...sessionsByKey.values()].sort(
    (a, b) => new Date(b.sessionEnd).getTime() - new Date(a.sessionEnd).getTime(),
  );
}

export function filterAdminConversationSessions(sessions = [], filters = {}) {
  const searchTokens = cleanText(filters.search, "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  return sessions.filter((session) => {
    if (filters.clinicName && session.clinicName !== filters.clinicName) return false;
    if (filters.practitionerId && session.praticienId !== filters.practitionerId) return false;
    if (filters.date && !String(session.sessionStart || "").startsWith(filters.date)) return false;
    if (filters.accessState === "accessed" && !session.hasAccessLog) return false;
    if (filters.accessState === "never_accessed" && session.hasAccessLog) return false;

    if (searchTokens.length > 0) {
      const haystack = [
        session.clinicName,
        session.practitionerName,
        session.patientName,
        session.phone,
        session.lastEventLabel,
      ]
        .join(" ")
        .toLowerCase();

      return searchTokens.every((token) => haystack.includes(token));
    }

    return true;
  });
}

export function buildAdminDecryptReason(reasonKind, detail) {
  const kind = cleanText(reasonKind, "Other");
  const cleanDetail = cleanText(detail, "");
  return cleanDetail ? `${kind} - ${cleanDetail}` : kind;
}
